package songwritersnotebook.app.screens

import songwritersnotebook.app.screens.components.*

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.calculateCentroid
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.AutoFixNormal
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.ChevronLeft
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.MusicNote
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Redo
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.Undo
import androidx.compose.material3.*
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.material.icons.filled.Piano
import androidx.compose.material.icons.filled.Remove
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.times
import songwritersnotebook.app.components.ChordPanel
import songwritersnotebook.app.components.ChordCreatorSheet
import songwritersnotebook.app.components.SaveStatusIndicator
import songwritersnotebook.app.components.StudioSettingsDialog
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.Track
import songwritersnotebook.app.data.TrackData
import songwritersnotebook.app.data.TrackItem
import songwritersnotebook.app.ui.theme.BlueAccent
import songwritersnotebook.app.ui.theme.GoldAccent
import songwritersnotebook.app.utils.ChordUtils
import songwritersnotebook.app.viewmodels.DragType
import songwritersnotebook.app.viewmodels.GuitarInputMode
import songwritersnotebook.app.viewmodels.StepInterval
import songwritersnotebook.app.viewmodels.StepIntervalType
import songwritersnotebook.app.viewmodels.StudioUiState
import songwritersnotebook.app.viewmodels.StudioViewModel
import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_guitar
import songwritersnotebookapp.composeapp.generated.resources.ic_rewind
import songwritersnotebookapp.composeapp.generated.resources.ic_bass
import songwritersnotebookapp.composeapp.generated.resources.ic_drum
import com.benasher44.uuid.uuid4
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.roundToInt

// KMP: Replaced android.graphics.Color with Compose Color
val noteColor = Color(0xFFF8E790) // Yellow
val chordColor = Color(0xFF86A5F8) // Blue
val bassNoteColor = Color(0xFFF2B8B5) // Red-ish
val drumNoteColor = Color(0xFFD7BDE2) // Purple-ish

data class LayoutItem(
    val item: TrackItem,
    val lane: Int,
    val lanesInGroup: Int
)

private enum class Axis { X, Y }

private data class ZoomRequest(val factor: Float, val anchorValue: Float, val anchorViewportPx: Float, val axis: Axis)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StudioScreen(
    song: Song?,
    songId: String,
    onBack: () -> Unit,
    viewModel: StudioViewModel,
    subscriptionTier: songwritersnotebook.app.billing.SubscriptionTier = songwritersnotebook.app.billing.SubscriptionTier.FREE,
    onNavigateToSubscription: () -> Unit = {}
) {
    val uiState by viewModel.uiState.collectAsState()
    
    DisposableEffect(Unit) {
        onDispose {
            viewModel.clearPresenceOnExit()
        }
    }
    
    LaunchedEffect(uiState.shouldExit) {
        if (uiState.shouldExit) {
            songwritersnotebook.app.utils.PlatformToast.show("Access to this song was revoked.")
            onBack()
        }
    }

    val settings = uiState.settings
    val tracks = uiState.tracks
    val effectiveReadOnly = uiState.isViewer

    val authUserId = viewModel.authUserId
    val sessionDeviceId = viewModel.sessionDeviceId
    val allPresences = uiState.presence.values.filter { it.deviceId != sessionDeviceId }

    // DIAGNOSTIC LOGGING: Pinpoint visibility in Studio
    if (uiState.presence.isNotEmpty()) {
        println("DEBUG_UI_STUDIO: Found Presence - Current: $sessionDeviceId")
        uiState.presence.values.forEach { 
            println("DEBUG_UI_STUDIO: Node: ${it.deviceId}, User: ${it.username}, Track: ${it.activeTrackId}, Item: ${it.activeTrackItemId}")
        }
    }

    LaunchedEffect(songId) {
        if (songId.isNotEmpty()) {
            delay(500) // Small stability delay to ensure navigation is fully committed
            viewModel.loadSongById(songId)
        }
    }

    LaunchedEffect(song?.id) {
        song?.id?.let { viewModel.loadSongById(it) }
        viewModel.warmUp()
    }

    // KMP: Handle null song by showing loading
    if (song == null || uiState.song == null) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator()
        }
        return
    }

    val dpPerBeat by remember(settings.zoomX) { mutableStateOf((60f * settings.zoomX).dp) }
    val trackHeight by remember(settings.zoomY) { mutableStateOf((100f * settings.zoomY).dp) }

    // --- Dynamic Timeline Length Calculation ---
    val beatsPerBar = uiState.settings.timeSignature.beats
    val lastBeat = tracks.flatMap { it.items }.maxOfOrNull { it.beat + it.duration } ?: 0f
    val lastItemMeasure = kotlin.math.ceil(lastBeat / beatsPerBar).toInt()

    val defaultBars = 100
    val dynamicThresholdBars = 80
    val bufferBars = 20

    val totalBars = if (lastItemMeasure >= dynamicThresholdBars) {
        lastItemMeasure + bufferBars
    } else {
        defaultBars
    }
    val totalBeats = totalBars * beatsPerBar
    val timelineWidth = totalBeats * dpPerBeat

    val horizontalScrollState = rememberScrollState()
    val verticalScrollState = rememberScrollState()
    val currentDpPerBeat by rememberUpdatedState(dpPerBeat)
    val currentHorizontalScrollState by rememberUpdatedState(horizontalScrollState)
    val coroutineScope = rememberCoroutineScope()
    val density = LocalDensity.current
    var timelineContainerCoordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }

    val strumSpeed by viewModel.strumSpeed.collectAsState()
    val showChordCreatorSheet by viewModel.showChordCreatorSheet.collectAsState()
    val chordToEdit by viewModel.chordToEdit.collectAsState()

    var zoomRequest by remember { mutableStateOf<ZoomRequest?>(null) }
    LaunchedEffect(settings.zoomX, settings.zoomY) {
        zoomRequest?.let { request ->
            when (request.axis) {
                Axis.X -> {
                    val newPixelsPerBeat = with(density) { dpPerBeat.toPx() }
                    val newContentAnchorPx = request.anchorValue * newPixelsPerBeat
                    val newScroll = newContentAnchorPx - request.anchorViewportPx
                    horizontalScrollState.animateScrollTo(newScroll.roundToInt())
                }
                Axis.Y -> {
                    val newTrackHeight = with(density) { trackHeight.toPx() }
                    val newContentAnchorPx = request.anchorValue * newTrackHeight
                    val newScroll = newContentAnchorPx - request.anchorViewportPx
                    verticalScrollState.animateScrollTo(newScroll.roundToInt())
                }
            }
            zoomRequest = null
        }
    }

    LaunchedEffect(song?.id) {
        if (song?.id == null || song.id == TutorialStep.NONE.name) return@LaunchedEffect

        // --- Enter Actions ---
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_INTRO) {
            if (uiState.showSettingsDialog) viewModel.onSettingsDismiss()
            viewModel.onTabSelected("Lyrics")
            viewModel.onCancelSelection()
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_TABS) {
            if (uiState.showSettingsDialog) viewModel.onSettingsDismiss()
            viewModel.onTabSelected("Notes")
            viewModel.onNoteClicked("C4")
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_LENGTH) {
            // Ensure something is armed so the Length/Interval controls are visible
            // AND ensure selection is empty so the 'Set Item' menu isn't hidden by 'Selection' menu
            viewModel.onCancelSelection() 
            if (viewModel.uiState.value.itemToSet == null) {
                viewModel.forceArmNote("C4")
            }
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_INTERVAL) {
            // Ensure something is armed and selection is empty
            viewModel.onCancelSelection()
            if (viewModel.uiState.value.itemToSet == null) {
                viewModel.forceArmNote("C4")
            }
        }
        TutorialManager.registerAction(TutorialStep.STUDIO_DRAG) {
            val currentTracks = viewModel.uiState.value.tracks
            val pianoTrack = currentTracks.find { it.name.lowercase().contains("piano") }
            if (pianoTrack != null) {
                viewModel.onOpenTrackSettings(pianoTrack.id)
            }
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_TRACK_SETTINGS) {
            val currentTracks = viewModel.uiState.value.tracks
            val pianoTrack = currentTracks.find { it.name.lowercase().contains("piano") }
            if (pianoTrack != null) {
                viewModel.onOpenTrackSettings(pianoTrack.id)
            }
            if (pianoTrack?.isCollapsed == true) {
                viewModel.onToggleTrackCollapse(pianoTrack.id)
            }
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_TRACK_MINIMIZE) {
            viewModel.onCancelSelection()
        }
        TutorialManager.registerEnterAction(TutorialStep.STUDIO_CONTEXT_MENU) {
            val itemId = "tutorial-c-chord"
            val currentState = viewModel.uiState.value
            if (itemId !in currentState.selectedTrackItemIds) {
                viewModel.toggleItemSelection(itemId)
            }
        }

        // --- Actions ---
        TutorialManager.registerAction(TutorialStep.STUDIO_INTRO) {
            // viewModel.onSettingsClicked() - Removed as requested, settings should only open on step 37
        }
        TutorialManager.registerAction(TutorialStep.STUDIO_SETTINGS) {
            viewModel.onSettingsClicked()
        }
        TutorialManager.registerAction(TutorialStep.STUDIO_TABS) {
            viewModel.onTabSelected("Notes")
            viewModel.onNoteClicked("C4")
        }
        TutorialManager.registerAction(TutorialStep.STUDIO_TRACK_MINIMIZE) {
            val currentTracks = viewModel.uiState.value.tracks
            val trackId = currentTracks.firstOrNull()?.id
            if (trackId != null) viewModel.onToggleTrackCollapse(trackId)
            
            // Prepare for next step (Context Menu)
            val itemId = "tutorial-c-chord"
            val currentState = viewModel.uiState.value
            if (itemId !in currentState.selectedTrackItemIds) {
                viewModel.toggleItemSelection(itemId)
            }
        }
        TutorialManager.registerAction(TutorialStep.STUDIO_CONTEXT_MENU) {
            val itemId = "tutorial-c-chord"
            val currentState = viewModel.uiState.value
            if (itemId in currentState.selectedTrackItemIds) {
                viewModel.toggleItemSelection(itemId) // Deselect on exit
            }
        }
    }

    LaunchedEffect(settings.playheadPosition) {
        if (settings.isPlaying) {
            val viewportWidth = horizontalScrollState.viewportSize
            val currentScroll = horizontalScrollState.value
            val playheadAbsolutePx = with(density) { (settings.playheadPosition * dpPerBeat).toPx() }
            val playheadScreenX = playheadAbsolutePx - currentScroll

            if (playheadScreenX > viewportWidth) {
                val newScrollPosition = currentScroll + viewportWidth
                coroutineScope.launch {
                    horizontalScrollState.scrollTo(newScrollPosition)
                }
            } else if (playheadScreenX < 0) {
                val newScrollPosition = (currentScroll - viewportWidth).coerceAtLeast(0)
                coroutineScope.launch {
                    horizontalScrollState.scrollTo(newScrollPosition)
                }
            }
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            text = uiState.song?.title ?: "Studio",
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.fillMaxWidth()
                        )
                        SaveStatusIndicator(
                            saveStatus = uiState.saveStatus,
                            onClick = { viewModel.saveSong() }
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = {
                        viewModel.saveSong()
                        onBack()
                    }) {
                        Icon(Icons.Filled.ArrowBack, contentDescription = "Go back")
                    }
                },
                actions = {
                    IconButton(onClick = { viewModel.onRewindClicked() }) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_rewind),
                            contentDescription = "Rewind"
                        )
                    }
                    IconButton(
                        onClick = { viewModel.onPlayPauseClicked() },
                        enabled = true
                    ) {
                        if (uiState.isLoadingSamples || uiState.isPreparing) {
                            if (uiState.isLoadingSamples) {
                                CircularProgressIndicator(
                                    progress = { uiState.sampleLoadingProgress },
                                    modifier = Modifier.size(24.dp),
                                    strokeWidth = 2.dp,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            } else {
                                // Indeterminate spinner for asset preparation (buffering)
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    strokeWidth = 2.dp,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                        } else {
                            Icon(
                                if (settings.isPlaying) Icons.Default.Pause else Icons.Default.PlayArrow,
                                contentDescription = "Play/Pause"
                            )
                        }
                    }
                    IconButton(onClick = { viewModel.onFastForwardClicked() }) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_rewind),
                            contentDescription = "Fast Forward",
                            modifier = Modifier.scale(scaleX = -1f, scaleY = 1f)
                        )
                    }
                    IconButton(onClick = { 
                            TutorialManager.nextStep()
                            viewModel.onSettingsClicked() 
                        },
                        modifier = Modifier.tutorialTarget(TutorialStep.STUDIO_SETTINGS)
                    ) {
                        Icon(Icons.Default.Settings, contentDescription = "Studio Settings")
                    }
                }
            )
        },
        bottomBar = {
            StudioBottomBar(
                activeTab = uiState.activeTab,
                onTabSelected = { tab -> viewModel.onTabSelected(tab) },
                song = uiState.song,
                tracks = uiState.tracks,
                itemToSet = uiState.itemToSet,
                onArmItem = { item -> viewModel.armItemForPlacement(item) },
                studioViewModel = viewModel,
                uiState = uiState,
                onSetItem = { viewModel.onSetItemClicked() },
                strumSpeed = strumSpeed,
                onInstrumentSelected = { viewModel.setInstrument(it) },
                onStrumSpeedChange = { /* viewModel.onStrumSpeedChange(it) */ },
                onShowChordCreatorSheetChange = { /* viewModel.onShowChordCreatorSheetChange(it) */ },
                onChordToEditChange = { /* viewModel.onChordToEditChange(it) */ },
                onClearSelectedNotes = { /* viewModel.onClearSelectedNotes() */ },
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize()
        ) {
             Column(modifier = Modifier.fillMaxSize()) {
                val isWeb = remember { songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true) }
                if (isWeb && subscriptionTier != songwritersnotebook.app.billing.SubscriptionTier.STUDIO) {
                    Surface(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                        color = MaterialTheme.colorScheme.tertiaryContainer,
                        shape = RoundedCornerShape(8.dp),
                        tonalElevation = 2.dp
                    ) {
                        Row(
                            modifier = Modifier.padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Default.Info, contentDescription = null, tint = MaterialTheme.colorScheme.onTertiaryContainer)
                            Spacer(Modifier.width(12.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    "Studio Subscription Required",
                                    style = MaterialTheme.typography.labelLarge,
                                    color = MaterialTheme.colorScheme.onTertiaryContainer,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    "Saving and syncing on the web is exclusive to Studio subscribers. Upgrade to keep your changes!",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onTertiaryContainer
                                )
                            }
                            TextButton(onClick = onNavigateToSubscription) {
                                Text("Upgrade")
                            }
                        }
                    }
                }

                // Ruler Row

                Row(Modifier.fillMaxWidth()) {
                    Row(
                        Modifier
                            .width(120.dp)
                            .height(32.dp)
                            .background(MaterialTheme.colorScheme.surfaceColorAtElevation(1.dp)),
                        horizontalArrangement = Arrangement.SpaceAround,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(
                            onClick = { viewModel.undo() },
                            enabled = uiState.canUndo
                        ) {
                            Icon(
                                Icons.Default.Undo, 
                                contentDescription = "Undo",
                                tint = if (uiState.canUndo) LocalContentColor.current else LocalContentColor.current.copy(alpha = 0.5f)
                            )
                        }
                        IconButton(
                            onClick = { viewModel.redo() },
                            enabled = uiState.canRedo
                        ) {
                            Icon(
                                Icons.Default.Redo, 
                                contentDescription = "Redo",
                                tint = if (uiState.canRedo) LocalContentColor.current else LocalContentColor.current.copy(alpha = 0.5f)
                            )
                        }
                    }
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .horizontalScroll(horizontalScrollState)
                            .pointerInput(Unit) {
                                detectTapGestures { offset ->
                                    val pixelsPerBeat = currentDpPerBeat.toPx()
                                    if (pixelsPerBeat > 0) {
                                        val newPosition = offset.x / pixelsPerBeat
                                        viewModel.onPlayheadSeek(newPosition)
                                    }
                                }
                            }
                    ) {
                        Ruler(
                            modifier = Modifier
                                .height(32.dp)
                                .width(timelineWidth),
                            totalBars = totalBars,
                            beatsPerBar = beatsPerBar,
                            dpPerBeat = dpPerBeat
                        )
                    }
                }

                // Main Content Row
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                ) {
                    // Left Panel: Track Headers
                    Column(
                        modifier = Modifier
                            .width(120.dp)
                            .verticalScroll(verticalScrollState)
                            .background(MaterialTheme.colorScheme.surfaceColorAtElevation(1.dp))
                    ) {
                        tracks.forEach { track ->
                            var showSettingsMenu by remember { mutableStateOf(false) }
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(if (track.isCollapsed) 48.dp else trackHeight)
                                    .border(
                                        width = 0.5.dp,
                                        color = MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
                                    )
                                    .padding(8.dp),
                                contentAlignment = Alignment.CenterStart
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    val trackNameColor = when {
                                        track.isSoloed -> MaterialTheme.colorScheme.primary
                                        track.isMuted -> MaterialTheme.colorScheme.error
                                        else -> LocalContentColor.current
                                    }
                                    
                                    val collaboratorsOnTrack = allPresences.filter { presence ->
                                        presence.activeTrackId == track.id || 
                                        track.items.any { item -> 
                                            item.id == presence.activeTrackItemId || 
                                            presence.selectedTrackItemIds.contains(item.id)
                                        }
                                    }

                                    Column(modifier = Modifier.weight(1f)) {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Text(
                                                text = track.name,
                                                style = MaterialTheme.typography.labelMedium,
                                                color = trackNameColor,
                                                maxLines = 1,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                            if (collaboratorsOnTrack.isNotEmpty()) {
                                                Spacer(Modifier.width(4.dp))
                                                // Presence dots
                                                collaboratorsOnTrack.take(3).forEach { presence ->
                                                    Box(
                                                        modifier = Modifier
                                                            .size(6.dp)
                                                            .background(Color(if (presence.color != 0) presence.color else songwritersnotebook.app.data.UserPresence.generateUserColor(presence.userId)), CircleShape)
                                                    )
                                                    Spacer(Modifier.width(2.dp))
                                                }
                                            }
                                        }
                                    }

                                    Column(horizontalAlignment = Alignment.End) {
                                        IconButton(
                                            onClick = {
                                                viewModel.onToggleTrackCollapse(track.id)
                                            },
                                            modifier = Modifier.size(24.dp).then(if (tracks.indexOf(track) == 0) Modifier.tutorialTarget(TutorialStep.STUDIO_TRACK_MINIMIZE) else Modifier)
                                        ) {
                                            Icon(
                                                if (track.isCollapsed) Icons.Default.ExpandMore else Icons.Default.ExpandLess,
                                                contentDescription = "Expand/Collapse Track"
                                            )
                                        }
                                        if (!track.isCollapsed) {
                                            IconButton(
                                                onClick = { showSettingsMenu = true },
                                                modifier = Modifier.size(24.dp).then(
                                                    if (track.name.lowercase().contains("piano")) Modifier.tutorialTarget(TutorialStep.STUDIO_TRACK_SETTINGS) else Modifier
                                                )
                                            ) {
                                                Icon(
                                                    Icons.Default.Settings,
                                                    contentDescription = "Track Settings"
                                                )
                                            }
                                            if (showSettingsMenu) {
                                                TrackSettingsMenu(
                                                    track = track,
                                                    onMuteChange = { isMuted ->
                                                        viewModel.onTrackMuteChanged(track.id, isMuted)
                                                    },
                                                    onSoloChange = { isSoloed ->
                                                        viewModel.onTrackSoloChanged(track.id, isSoloed)
                                                    },
                                                    onVolumeChange = { volume ->
                                                        viewModel.onTrackVolumeChanged(track.id, volume)
                                                    },
                                                    onStrumSpeedChange = { speed ->
                                                        viewModel.onTrackStrumSpeedChanged(track.id, speed)
                                                    },
                                                    onDismiss = { showSettingsMenu = false }
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Right Panel: Timeline
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .horizontalScroll(
                                horizontalScrollState,
                                enabled = !settings.isPlaying && !uiState.isLassoModeActive
                            )
                            .verticalScroll(
                                verticalScrollState,
                                enabled = !uiState.isLassoModeActive
                            )
                            .pointerInput(settings.isPlaying) {
                                if (!settings.isPlaying) {
                                    awaitEachGesture {
                                        // Wait for at least two pointers to be down before starting the gesture
                                        var event = awaitPointerEvent()
                                        while (event.changes.size < 2) {
                                            event = awaitPointerEvent()
                                        }

                                        // --- GESTURE STARTED: CAPTURE THE INITIAL STATE ---
                                        val initialContentCentroid = event.calculateCentroid()
                                        val initialScroll = Offset(
                                            horizontalScrollState.value.toFloat(),
                                            verticalScrollState.value.toFloat()
                                        )
                                        // This is the point on the screen (viewport) we want to keep stable
                                        val viewportAnchor = initialContentCentroid - initialScroll
                                        // This is the point on the content we are tracking
                                        val contentAnchor = initialContentCentroid

                                        var cumulativeZoom = 1.0f
                                        var lockedAxis: Axis? = null

                                        // --- GESTURE CONTINUES: PROCESS SUBSEQUENT EVENTS ---
                                        do {
                                            event = awaitPointerEvent()
                                            val pointers = event.changes.filter { it.pressed }
                                            if (pointers.size < 2) break // End gesture if a finger is lifted

                                            val p1 = pointers[0]
                                            val p2 = pointers[1]
                                            val xDist = abs(p1.position.x - p2.position.x)
                                            val prevXDist = abs(p1.previousPosition.x - p2.previousPosition.x)
                                            val yDist = abs(p1.position.y - p2.position.y)
                                            val prevYDist = abs(p1.previousPosition.y - p2.previousPosition.y)

                                            if (lockedAxis == null) {
                                                val xChange = abs(xDist - prevXDist)
                                                val yChange = abs(yDist - prevYDist)
                                                if (xChange > 1f || yChange > 1f) {
                                                    lockedAxis = if (xChange > yChange) Axis.X else Axis.Y
                                                }
                                            }

                                            when (lockedAxis) {
                                                Axis.X -> {
                                                    if (prevXDist > 0.1f) {
                                                        val incrementalZoom = xDist / prevXDist
                                                        if (incrementalZoom.isFinite() && incrementalZoom != 1f) {
                                                            cumulativeZoom *= incrementalZoom
                                                            viewModel.onZoomChange(incrementalZoom, 1f)

                                                            val newContentAnchorPosition = contentAnchor.x * cumulativeZoom
                                                            val newScroll = newContentAnchorPosition - viewportAnchor.x

                                                            coroutineScope.launch {
                                                                horizontalScrollState.scrollTo(newScroll.roundToInt())
                                                            }
                                                        }
                                                    }
                                                }
                                                Axis.Y -> {
                                                    if (prevYDist > 0.1f) {
                                                        val incrementalZoom = yDist / prevYDist
                                                        if (incrementalZoom.isFinite() && incrementalZoom != 1f) {
                                                            cumulativeZoom *= incrementalZoom
                                                            viewModel.onZoomChange(1f, incrementalZoom)

                                                            val newContentAnchorPosition = contentAnchor.y * cumulativeZoom
                                                            val newScroll = newContentAnchorPosition - viewportAnchor.y

                                                            coroutineScope.launch {
                                                                verticalScrollState.scrollTo(newScroll.roundToInt())
                                                            }
                                                        }
                                                    }
                                                }
                                                null -> { /* Wait for axis lock */ }
                                            }
                                            pointers.forEach { it.consume() }
                                        } while (pointers.all { it.pressed })
                                    }
                                }
                            }
                    ) {
                        val totalTrackHeight = remember(tracks, trackHeight) {
                            tracks.sumOf { if (it.isCollapsed) 48.0 else trackHeight.value.toDouble() }.dp
                        }
                        // KMP: Replaced mainViewModel.chordLibrary
                        val currentChordLibrary = ChordUtils.PIANO_CHORD_VOICINGS.keys.toList()

                        Box(
                            modifier = Modifier
                                .width(timelineWidth)
                                .height(totalTrackHeight)
                                .onGloballyPositioned { timelineContainerCoordinates = it }
                        ) {
                            TimelineGrid(
                                modifier = Modifier.matchParentSize(),
                                totalBars = totalBars,
                                beatsPerBar = beatsPerBar,
                                dpPerBeat = dpPerBeat,
                                trackCount = tracks.size,
                                trackHeight = trackHeight,
                                isSnappingOn = uiState.settings.isSnappingOn,
                                snapInterval = uiState.snapInterval
                            )
                            Column {
                                tracks.forEach { track ->
                                    val currentTrackHeight = if (track.isCollapsed) 48.dp else trackHeight
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(currentTrackHeight)
                                    ) {
                                        val layoutItems = remember(track.items) {
                                            calculateItemLayout(track.items, currentChordLibrary)
                                        }

                                        layoutItems.forEach { layoutItem ->
                                            TrackItemView(
                                                layoutItem = layoutItem,
                                                dpPerBeat = dpPerBeat,
                                                trackHeight = trackHeight,
                                                studioViewModel = viewModel,
                                                isSelected = layoutItem.item.id in uiState.selectedTrackItemIds,
                                                isCollapsed = track.isCollapsed,
                                                modifier = if (layoutItem.item.id == "tutorial-c-chord") Modifier.tutorialTarget(TutorialStep.STUDIO_DRAG) else Modifier,
                                                onItemClick = {
                                                    if (!track.isCollapsed) {
                                                        viewModel.toggleItemSelection(layoutItem.item.id)
                                                    }
                                                },
                                                onLocated = { layoutCoordinates ->
                                                    timelineContainerCoordinates?.let { container ->
                                                        val containerTopLeftInWindow = container.localToWindow(Offset.Zero)
                                                        val itemRectInWindow = layoutCoordinates.boundsInWindow()
                                                        val rect = itemRectInWindow.translate(-containerTopLeftInWindow.x, -containerTopLeftInWindow.y)
                                                        viewModel.onTrackItemLayout(layoutItem.item.id, rect)
                                                    }
                                                },
                                                allPresences = allPresences
                                            )
                                        }
                                    }
                                }
                            }

                            val targetPlayheadX = dpPerBeat * settings.playheadPosition
                            val animatedPlayheadX by animateDpAsState(
                                targetValue = targetPlayheadX,
                                animationSpec = tween(durationMillis = 16)
                            )

                            Playhead(
                                modifier = Modifier.fillMaxHeight(),
                                playheadX = animatedPlayheadX,
                                playheadPosition = settings.playheadPosition,
                                dpPerBeat = dpPerBeat,
                                totalHeight = totalTrackHeight,
                                onSeek = { newPosition -> viewModel.onPlayheadSeek(newPosition) },
                                totalBars = totalBars,
                                beatsPerBar = beatsPerBar
                            )
                            
                            LassoOverlay(
                                isLassoModeActive = uiState.isLassoModeActive,
                                lassoPath = uiState.lassoPath,
                                onLassoDrawStart = viewModel::onLassoDrawStart,
                                onLassoDraw = viewModel::onLassoDraw,
                                onLassoDrawEnd = { pixelsPerBeat, trackHeightPx ->
                                    viewModel.onLassoDrawEnd(pixelsPerBeat, trackHeightPx)
                                },
                                modifier = Modifier.matchParentSize(),
                                horizontalScrollState = horizontalScrollState,
                                verticalScrollState = verticalScrollState,
                                pixelsPerBeat = with(density) { dpPerBeat.toPx() },
                                trackHeightPx = with(density) { trackHeight.toPx() }
                            )
                        }
                    }
                }
            }
            
            uiState.editingInterval?.let { type ->
                val interval = if (type == StepIntervalType.LENGTH) uiState.noteLength else uiState.noteInterval
                IntervalSliderDialog(
                    initialInterval = interval,
                    label = if (type == StepIntervalType.LENGTH) "Note Length" else "Seek Interval",
                    onDismiss = { viewModel.onDismissIntervalEditor() },
                    onSave = { newInterval ->
                        viewModel.onIntervalChanged(type, newInterval)
                        viewModel.onDismissIntervalEditor()
                    }
                )
            }
        }
    }
    
    if (uiState.showSettingsDialog) {
        StudioSettingsDialog(
            settings = uiState.settings,
            isMetronomeOn = uiState.isMetronomeOn,
            isMetronomeMuted = uiState.isMetronomeMuted,
            isTempoPreviewActive = uiState.isTempoPreviewActive,
            currentMetronomeBeat = uiState.currentMetronomeBeat,
            isSnappingOn = uiState.settings.isSnappingOn,
            snapInterval = uiState.snapInterval,
            onDismiss = { viewModel.onSettingsDismiss() },
            onBpmChange = { viewModel.onBpmChange(it) },
            onTimeSignatureChange = { beats, per -> viewModel.onTimeSignatureChange(beats, per) },
            onMetronomeToggle = { viewModel.onMetronomeToggle(it) },
            onMetronomeMuteToggle = { viewModel.toggleMetronomeMuted() },
            onTempoPreviewToggle = { viewModel.toggleTempoPreview() },
            onSnappingToggle = { viewModel.onSnappingToggle(it) },
            onSnapIntervalChange = { newInterval -> viewModel.onSnapIntervalChange(newInterval) }
        )
    }

    LaunchedEffect(uiState.showSettingsDialog) {
        if (!uiState.showSettingsDialog) {
            viewModel.stopTempoPreview()
        }
    }
    
    if (showChordCreatorSheet) {
        ChordCreatorSheet(
            initialChordName = chordToEdit?.first,
            chordCreatorUiState = uiState.chordCreatorUiState,
            onDismiss = { viewModel.onShowChordCreatorSheetChange(false) },
            onSave = { name ->
                viewModel.onSaveChord(name)
                viewModel.onShowChordCreatorSheetChange(false)
            },
            onPlayCurrentChord = { viewModel.playCurrentChordInCreator() },
            onShuffleChord = { viewModel.shuffleChordSelection() },
            onPresetSelected = { root, quality -> viewModel.onPresetChordSelected(root, quality) },
            onSelectQualityCategory = { index -> viewModel.selectQualityCategory(index) },
            onPlayPreset = { root, quality -> viewModel.playChordPreset(root, quality) },
            fretWindowStart = uiState.fretWindowStart,
            onFretWindowChange = { delta -> viewModel.onFretWindowChange(delta) },
            onOctaveChange = { delta -> viewModel.setKeyboardOctave(delta) },
            onClearManualKeys = { viewModel.clearManualKeys() },
            onToggleManualKey = { note -> viewModel.toggleManualKey(note) },
            onTabChange = { tab -> viewModel.onTabChange(tab) },
            onTuningChange = { index, step -> viewModel.onTuningChange(index, step) },
            onCapoChange = { capo -> viewModel.onCapoChange(capo) },
            onResetTuning = { viewModel.resetTuningToStandard() },
            onCustomToggle = { viewModel.onCustomToggle() },
            onInit = { name -> viewModel.initChordCreator(name) },
            isFavorited = uiState.isFavorited,
            onToggleFavorite = { viewModel.toggleFavoriteChord(it) },
            isLeftHanded = uiState.isLeftHanded
        )
    }
}

@Composable
fun Ruler(
    modifier: Modifier = Modifier,
    totalBars: Int,
    beatsPerBar: Int,
    dpPerBeat: Dp
) {
    val density = LocalDensity.current
    val textMeasurer = rememberTextMeasurer()
    val textStyle = MaterialTheme.typography.labelSmall.copy(color = Color.Gray)

    Canvas(modifier = modifier) {
        val height = size.height
        val pixelsPerBeat = dpPerBeat.toPx()

        for (bar in 0..totalBars) {
            val x = bar * beatsPerBar * pixelsPerBeat
            drawLine(
                color = Color.Gray,
                start = Offset(x, 0f),
                end = Offset(x, height),
                strokeWidth = 1.dp.toPx()
            )
            
            val text = (bar + 1).toString()
            val textLayoutResult = textMeasurer.measure(
                text = text,
                style = textStyle
            )
            
            drawText(
                textLayoutResult = textLayoutResult,
                topLeft = Offset(x + 4.dp.toPx(), height / 2 - textLayoutResult.size.height / 2)
            )
        }
    }
}

@Composable
fun Playhead(
    modifier: Modifier = Modifier,
    playheadX: Dp,
    playheadPosition: Float,
    dpPerBeat: Dp,
    totalHeight: Dp,
    onSeek: (Float) -> Unit,
    totalBars: Int,
    beatsPerBar: Int
) {
    val density = LocalDensity.current
    var dragOffsetPx by remember { mutableStateOf(0f) }
    val finalPlayheadX = (playheadX + with(density) { dragOffsetPx.toDp() }).coerceAtLeast(0.dp)

    Box(modifier = modifier.offset(x = finalPlayheadX)) {
        Box(
            modifier = Modifier
                .width(2.dp)
                .height(totalHeight)
                .background(Color.Red)
        )
        Box(
            modifier = Modifier
                .offset(x = (-20).dp, y = (-8).dp)
                .size(40.dp)
                .pointerInput(playheadPosition) {
                    detectDragGestures(
                        onDragStart = { dragOffsetPx = 0f },
                        onDragEnd = {
                            val totalOffsetInBeats = dragOffsetPx / dpPerBeat.toPx()
                            val newPosition = (playheadPosition + totalOffsetInBeats).coerceAtLeast(0f)
                            onSeek(newPosition)
                            dragOffsetPx = 0f
                        },
                        onDragCancel = { dragOffsetPx = 0f },
                        onDrag = { change, dragAmount ->
                            change.consume()
                            dragOffsetPx += dragAmount.x
                        }
                    )
                }
        ) {
            Box(
                modifier = Modifier
                    .align(Alignment.Center)
                    .size(24.dp)
                    .background(Color.Red, shape = CircleShape)
                    .border(2.dp, Color.White, CircleShape)
            )
        }
    }
}

@Composable
fun TrackItemView(
    layoutItem: LayoutItem,
    dpPerBeat: Dp,
    trackHeight: Dp,
    studioViewModel: StudioViewModel,
    isSelected: Boolean,
    isCollapsed: Boolean,
    onItemClick: () -> Unit,
    onLocated: (LayoutCoordinates) -> Unit = {},
    allPresences: List<songwritersnotebook.app.data.UserPresence> = emptyList(),
    modifier: Modifier = Modifier
) {
    val item = layoutItem.item
    val density = LocalDensity.current
    val uiState by studioViewModel.uiState.collectAsState()

    val (backgroundColor, contentColor) = when (item.type) {
        "lyric" -> MaterialTheme.colorScheme.primaryContainer to MaterialTheme.colorScheme.onPrimaryContainer
        "chord" -> chordColor to Color.Black
        "note" -> {
            when (item.data.instrument?.lowercase()) {
                "piano", "guitar" -> noteColor to Color.Black
                "bass" -> bassNoteColor to Color.Black
                "drums" -> drumNoteColor to Color.Black
                else -> MaterialTheme.colorScheme.tertiaryContainer to MaterialTheme.colorScheme.onTertiaryContainer
            }
        }
        "recording" -> MaterialTheme.colorScheme.errorContainer to MaterialTheme.colorScheme.onErrorContainer
        else -> MaterialTheme.colorScheme.tertiaryContainer to MaterialTheme.colorScheme.onTertiaryContainer
    }
    
    // Dim if user is a viewer
    val isReadOnly = uiState.isViewer
    val finalBackgroundColor = if (isReadOnly) backgroundColor.copy(alpha = 0.5f) else backgroundColor

    if (isCollapsed) {
        val lineHeight = 4.dp
        val verticalSpacing = 2.dp
        val yOffset = layoutItem.lane * (lineHeight + verticalSpacing)

        Box(
            modifier = Modifier
                .offset(
                    x = dpPerBeat * item.beat.coerceAtLeast(0f),
                    y = yOffset
                )
                .width(dpPerBeat * item.duration.coerceAtLeast(0.1f))
                .height(lineHeight)
                .background(finalBackgroundColor, shape = RoundedCornerShape(2.dp))
        )
    } else {
        val numLanes = layoutItem.lanesInGroup.coerceAtLeast(1)
        val verticalMargin = 2.dp
        val laneHeight = (trackHeight / numLanes) - verticalMargin

        var dragOffsetPx by remember { mutableStateOf(0f) }
        val isBeingDragged = uiState.dragState?.items?.any { it.id == item.id } == true

        val itemBaseBeat = if (isBeingDragged) uiState.dragState?.items?.find { it.id == item.id }?.initialBeat ?: item.beat else item.beat
        val itemBaseDuration = if (isBeingDragged) uiState.dragState?.items?.find { it.id == item.id }?.initialDuration ?: item.duration else item.duration

        val dragDeltaBeats = with(density) { dragOffsetPx / dpPerBeat.toPx() }

        // Helper for visual snapping
        val snapInterval = remember(uiState.settings.isSnappingOn, uiState.settings.snapInterval, uiState.settings.timeSignature) {
            val s = uiState.settings
            if (!s.isSnappingOn || s.snapInterval.denominator == 0) null
            else {
                val ratio = s.snapInterval.numerator.toFloat() / s.snapInterval.denominator.toFloat()
                if (s.snapInterval.base == "bar") ratio * s.timeSignature.beats else ratio
            }
        }

        fun snap(value: Float): Float {
            return if (snapInterval != null && snapInterval > 0f) {
                (value / snapInterval).roundToInt() * snapInterval
            } else value
        }

        val finalBeat: Float
        val finalDuration: Float

        if (isBeingDragged) {
            when (uiState.dragState?.type) {
                DragType.MOVE -> {
                    finalBeat = snap(itemBaseBeat + dragDeltaBeats)
                    finalDuration = itemBaseDuration
                }
                DragType.RESIZE_END -> {
                    finalBeat = itemBaseBeat
                    val rawDuration = itemBaseDuration + dragDeltaBeats
                    finalDuration = if (snapInterval != null) snap(rawDuration).coerceAtLeast(snapInterval) else rawDuration
                }
                DragType.RESIZE_START -> {
                    val rawBeat = itemBaseBeat + dragDeltaBeats
                    val snappedBeat = snap(rawBeat)
                    finalBeat = snappedBeat
                    val end = itemBaseBeat + itemBaseDuration
                    finalDuration = (end - finalBeat).coerceAtLeast(if (snapInterval != null) snapInterval else 0.1f)
                }
                null -> {
                    finalBeat = item.beat
                    finalDuration = item.duration
                }
            }
        } else {
            finalBeat = item.beat
            finalDuration = item.duration
        }

        fun Modifier.dragDetector(dragType: DragType): Modifier {
            if (isReadOnly) return this // Disable dragging if read-only
            return pointerInput(item.id, dragType, dpPerBeat) {
                detectDragGestures(
                    onDragStart = {
                        dragOffsetPx = 0f
                        studioViewModel.onItemDragStart(item.id, dragType)
                    },
                    onDragEnd = {
                        val pixelsPerBeat = with(density) { dpPerBeat.toPx() }
                        studioViewModel.onItemDragEnd(dragOffsetPx, pixelsPerBeat)
                        dragOffsetPx = 0f
                    },
                    onDragCancel = {
                        dragOffsetPx = 0f
                        studioViewModel.onItemDragEnd(0f, 0f)
                    },
                    onDrag = { change, dragAmount ->
                        change.consume()
                        dragOffsetPx += dragAmount.x
                    }
                )
            }
        }
        
        // Collaborative Selection
        val collaboratorPresence = allPresences.find { presence ->
            (presence.activeTrackItemId == item.id || presence.selectedTrackItemIds.contains(item.id))
        }
        val collaboratorColor = collaboratorPresence?.let { Color(it.color) }

        Box(
            modifier = modifier
                .offset(x = dpPerBeat * finalBeat.coerceAtLeast(0f), y = (layoutItem.lane * (laneHeight + verticalMargin)))
                .onGloballyPositioned { onLocated(it) }
                .width(dpPerBeat * finalDuration.coerceAtLeast(0.1f))
                .height(laneHeight)
                .background(finalBackgroundColor, shape = RoundedCornerShape(4.dp))
                .border(
                    width = if (isSelected || collaboratorColor != null) 2.dp else 0.dp,
                    color = when {
                        isSelected -> MaterialTheme.colorScheme.primary
                        collaboratorColor != null -> collaboratorColor
                        else -> Color.Transparent
                    },
                    shape = RoundedCornerShape(4.dp)
                )
                .pointerInput(item.id, isReadOnly) {
                    if (!isReadOnly) {
                        detectTapGestures(onTap = { onItemClick() })
                    }
                }
                .dragDetector(DragType.MOVE)
                .padding(horizontal = 4.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            if (item.type == "recording") {
                val waveformPoints = uiState.waveformCache[item.data.url] ?: emptyList()
                val (trimStartPercentage, visiblePercentage) = remember(item, finalBeat, finalDuration, itemBaseBeat) {
                    if (item.originalDurationBeats != null && item.originalDurationBeats!! > 0) {
                        val dynamicTrimStart = item.trimStartBeats + (finalBeat - itemBaseBeat)
                        val startPercent = (dynamicTrimStart / item.originalDurationBeats!!).coerceIn(0f, 1f)
                        val visiblePercent = (finalDuration / item.originalDurationBeats!!).coerceIn(0f, 1f)
                        startPercent to visiblePercent
                    } else {
                        0f to 1f
                    }
                }
                Waveform(
                    modifier = Modifier.fillMaxSize(),
                    waveformPoints = waveformPoints,
                    color = contentColor,
                    trimStartPercentage = trimStartPercentage,
                    visiblePercentage = visiblePercentage
                )
            }

            Text(
                text = item.data.name ?: item.data.text ?: "Item",
                color = contentColor,
                style = MaterialTheme.typography.labelSmall,
                maxLines = if (item.type == "lyric") Int.MAX_VALUE else 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier
                    .align(if (item.type == "recording") Alignment.TopStart else Alignment.CenterStart)
                    .padding(horizontal = 8.dp)
                    .then(if (item.type == "recording") Modifier.padding(top = 2.dp) else Modifier)
            )

            val handleWidth = 12.dp
            val handleColor = Color.White.copy(alpha = 0.7f)

            Box(
                modifier = Modifier
                    .align(Alignment.CenterStart)
                    .offset(x = (-4).dp)
                    .width(handleWidth)
                    .fillMaxHeight()
                    .dragDetector(DragType.RESIZE_START)
            ) {
                Box(
                    modifier = Modifier
                        .align(Alignment.Center)
                        .width(2.dp)
                        .height(laneHeight * 0.4f)
                        .background(handleColor, CircleShape)
                )
            }

            Box(
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .offset(x = 4.dp)
                    .width(handleWidth)
                    .fillMaxHeight()
                    .dragDetector(DragType.RESIZE_END)
            ) {
                Box(
                    modifier = Modifier
                        .align(Alignment.Center)
                        .width(2.dp)
                        .height(laneHeight * 0.4f)
                        .background(handleColor, CircleShape)
                )
            }
        }
    }
}

@Composable
fun Waveform(
    modifier: Modifier = Modifier,
    waveformPoints: List<Float>,
    color: Color,
    strokeWidth: Float = 1f,
    trimStartPercentage: Float = 0f,
    visiblePercentage: Float = 1f
) {
    Canvas(modifier = modifier) {
        drawWaveform(waveformPoints, color, strokeWidth, trimStartPercentage, visiblePercentage)
    }
}

private fun DrawScope.drawWaveform(
    waveformPoints: List<Float>,
    color: Color,
    strokeWidth: Float,
    trimStartPercentage: Float,
    visiblePercentage: Float
) {
    if (waveformPoints.isEmpty()) return

    val maxAmplitude = waveformPoints.maxOrNull()?.coerceAtLeast(1f) ?: 1f
    val verticalCenter = size.height / 2

    val totalPoints = waveformPoints.size
    val startPointIndex = (totalPoints * trimStartPercentage).toInt()
    val visiblePointsCount = (totalPoints * visiblePercentage).toInt()
    val endPointIndex = (startPointIndex + visiblePointsCount).coerceAtMost(totalPoints)
    val visibleWaveform = waveformPoints.subList(startPointIndex, endPointIndex)
    if (visibleWaveform.isEmpty()) return

    val topPath = Path()
    topPath.moveTo(0f, verticalCenter)
    visibleWaveform.forEachIndexed { index, point ->
        // SANITIZATION: Protect against NaN or Infinite values which crash Skiko/Web
        val safePoint = if (point.isNaN() || point.isInfinite()) 0f else point
        val x = if (visibleWaveform.size > 1) {
            (index.toFloat() / (visibleWaveform.size - 1)) * size.width
        } else 0f
        val yOffset = (safePoint / maxAmplitude) * verticalCenter
        topPath.lineTo(x, (verticalCenter - yOffset).takeIf { !it.isNaN() && !it.isInfinite() } ?: verticalCenter)
    }

    val bottomPath = Path()
    bottomPath.moveTo(0f, verticalCenter)
    visibleWaveform.forEachIndexed { index, point ->
        // SANITIZATION: Protect against NaN or Infinite values
        val safePoint = if (point.isNaN() || point.isInfinite()) 0f else point
        val x = if (visibleWaveform.size > 1) {
            (index.toFloat() / (visibleWaveform.size - 1)) * size.width
        } else 0f
        val yOffset = (safePoint / maxAmplitude) * verticalCenter
        bottomPath.lineTo(x, (verticalCenter + yOffset).takeIf { !it.isNaN() && !it.isInfinite() } ?: verticalCenter)
    }

    val strokeStyle = androidx.compose.ui.graphics.drawscope.Stroke(
        width = strokeWidth,
        cap = StrokeCap.Round
    )

    drawPath(path = topPath, color = color, style = strokeStyle)
    drawPath(path = bottomPath, color = color, style = strokeStyle)
}

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun StudioBottomBar(
    activeTab: String,
    onTabSelected: (String) -> Unit,
    song: Song?,
    tracks: List<Track>,
    itemToSet: TrackItem?,
    onArmItem: (TrackItem) -> Unit,
    studioViewModel: StudioViewModel,
    uiState: StudioUiState,
    onSetItem: () -> Unit,
    strumSpeed: Float,
    onInstrumentSelected: (String) -> Unit,
    onStrumSpeedChange: (Float) -> Unit,
    onShowChordCreatorSheetChange: (Boolean) -> Unit,
    onChordToEditChange: (Pair<String, List<String>>?) -> Unit,
    onClearSelectedNotes: () -> Unit,
) {
    val tabs = listOf("Lyrics", "Recordings", "Chords", "Notes")
    var contentHeight by remember { mutableStateOf(250.dp) }
    val density = LocalDensity.current

    val handleHeight = 24.dp
    // Determine which menu (if any) is active to calculate total height
    val setItemMenuHeight = if (uiState.itemToSet != null && uiState.selectedTrackItemIds.isEmpty()) 64.dp else 0.dp
    val selectionMenuHeight = if (uiState.selectedTrackItemIds.isNotEmpty()) 80.dp else 0.dp
    
    // If a menu is active, we might hide the main content or keep it. 
    // The original app hides the tab content when items are selected, but keeps it for "Set Item".
    // Let's follow the original structure:
    val mainContentHeight = if (uiState.selectedTrackItemIds.isEmpty()) 48.dp + contentHeight else 0.dp
    val totalHeight = handleHeight + setItemMenuHeight + selectionMenuHeight + mainContentHeight

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .height(totalHeight)
            .background(MaterialTheme.colorScheme.surfaceColorAtElevation(3.dp))
    ) {
        // --- 1. Resize Handle ---
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(handleHeight)
                .pointerInput(Unit) {
                    detectVerticalDragGestures { _, dragAmount ->
                        // dragging up is negative dy, but we want to increase height
                        val newHeight = contentHeight - (dragAmount / density.density).dp
                        contentHeight = newHeight.coerceIn(100.dp, 600.dp)
                    }
                }
                .background(MaterialTheme.colorScheme.surfaceColorAtElevation(6.dp)),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .width(40.dp)
                    .height(4.dp)
                    .background(MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f), CircleShape)
            )
        }

        // --- 2. Context Menus ---
        val isSelectionActive = uiState.selectedTrackItemIds.isNotEmpty()
        if (isSelectionActive) {
            SelectionContextMenu(
                    count = uiState.selectedTrackItemIds.size,
                    onDelete = { studioViewModel.onDeleteSelectedItems() },
                    onCancel = { studioViewModel.onCancelSelection() },
                    onNudge = { direction -> studioViewModel.onNudgeSelectedItems(direction) },
                    onCopy = { studioViewModel.onCopySelectedItems() },
                    onLassoModeToggled = { studioViewModel.onLassoModeToggled() },
                    isLassoModeActive = uiState.isLassoModeActive
                )
            } else {
                Column {
                    // Set Item Menu
                    if (uiState.itemToSet != null) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(64.dp)
                                .padding(horizontal = 16.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                IconButton(onClick = { studioViewModel.onSeekPreviousClicked() }) {
                                    Icon(Icons.Default.ChevronLeft, "Seek Prev")
                                }
                                Button(
                                    onClick = onSetItem,
                                    enabled = uiState.itemToSet != null,
                                ) {
                                    Icon(Icons.Default.Check, contentDescription = "Set Item")
                                    Spacer(Modifier.width(4.dp))
                                    Text("Set")
                                }
                                IconButton(onClick = { studioViewModel.onSeekNextClicked() }) {
                                    Icon(Icons.Default.ChevronRight, "Seek Next")
                                }
                            }
                            Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                StepSettingControl(
                                    label = "Length",
                                    interval = uiState.noteLength,
                                    onClick = {
                                        TutorialManager.nextStep()
                                        studioViewModel.onEditIntervalClicked(StepIntervalType.LENGTH)
                                    },
                                    modifier = Modifier.tutorialTarget(TutorialStep.STUDIO_LENGTH)
                                )
                                StepSettingControl(
                                    label = "Interval",
                                    interval = uiState.noteInterval,
                                    onClick = {
                                        TutorialManager.nextStep()
                                        studioViewModel.onEditIntervalClicked(StepIntervalType.INTERVAL)
                                    },
                                    modifier = Modifier.tutorialTarget(TutorialStep.STUDIO_INTERVAL)
                                )
                            }
                        }
                    }

                    // --- 3. Tabs & Content ---
                    TabRow(
                        selectedTabIndex = tabs.indexOf(activeTab),
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.tutorialTarget(TutorialStep.STUDIO_TABS)
                    ) {
                        tabs.forEach { title ->
                            Tab(
                                selected = activeTab == title,
                                onClick = { onTabSelected(title) },
                                text = { Text(title, style = MaterialTheme.typography.labelMedium) }
                            )
                        }
                    }

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(contentHeight)
                            .padding(8.dp),
                    ) {
                        when (activeTab) {
                            "Lyrics" -> {
                                if (song != null) {
                                    LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                        song.sections.forEach { section ->
                                            items(section.lines, key = { it.id }) { line ->
                                                val lyricText = line.text.replace(Regex("\\[.*?\\]"), "").trim()
                                                if (lyricText.isNotEmpty()) {
                                                    val item = TrackItem(
                                                        id = line.id,
                                                        beat = 0f,
                                                        duration = 4f,
                                                        type = "lyric",
                                                        data = TrackData(
                                                            text = lyricText,
                                                            section = section.title,
                                                            line = section.lines.indexOf(line) + 1,
                                                            originalLineText = line.text
                                                        )
                                                    )
                                                    val isArmed = itemToSet?.data?.text == item.data.text && itemToSet?.data?.section == item.data.section

                                                    Button(
                                                        onClick = { onArmItem(item) },
                                                        modifier = Modifier.fillMaxWidth(),
                                                        shape = RoundedCornerShape(8.dp),
                                                        colors = if (isArmed) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(),
                                                        border = if (!isArmed) BorderStroke(1.dp, MaterialTheme.colorScheme.outline) else null
                                                    ) {
                                                        Column(modifier = Modifier.fillMaxWidth()) {
                                                            Text(
                                                                text = "${section.title} - Line ${section.lines.indexOf(line) + 1}",
                                                                style = MaterialTheme.typography.labelSmall,
                                                                fontWeight = FontWeight.Bold
                                                            )
                                                            Text(
                                                                text = lyricText,
                                                                maxLines = 1,
                                                                overflow = TextOverflow.Ellipsis,
                                                                style = MaterialTheme.typography.bodySmall
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                } else {
                                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                        CircularProgressIndicator()
                                    }
                                }
                            }
                            "Chords" -> {
                                val currentSong = uiState.song ?: Song.createEmpty()
                                val chordNames = uiState.orderedChordNames.ifEmpty { currentSong.chordDefinitions.keys.sorted() }
                                val selectedChordForActions = if (uiState.itemToSet?.type == "chord") uiState.itemToSet.data.name else null

                                ChordPanel(
                                    orderedChordNames = chordNames,
                                    chordLibrary = currentSong.chordDefinitions,
                                    favoriteChords = uiState.favoriteChords,
                                    selectedChord = selectedChordForActions,
                                    onChordClick = { chordName ->
                                        val item = TrackItem(
                                            id = uuid4().toString(),
                                            beat = 0f,
                                            duration = 1f,
                                            type = "chord",
                                            data = TrackData(
                                                name = chordName,
                                                instrument = uiState.instrument
                                            )
                                        )
                                        onArmItem(item)
                                        if (selectedChordForActions != chordName) {
                                            studioViewModel.playChordPreview(chordName, uiState.instrument, strumSpeed)
                                        }
                                    },
                                    onAddChordClick = {
                                        studioViewModel.initChordCreator(null)
                                        studioViewModel.onChordToEditChange(null)
                                        studioViewModel.onShowChordCreatorSheetChange(true)
                                    },
                                    onEditChord = {
                                        if (selectedChordForActions != null) {
                                            studioViewModel.initChordCreator(selectedChordForActions)
                                            studioViewModel.onChordToEditChange(selectedChordForActions to emptyList())
                                            studioViewModel.onShowChordCreatorSheetChange(true)
                                        }
                                    },
                                    chordsInUse = uiState.chordsInUse,
                                    onDeleteChord = { chord -> studioViewModel.deleteChord(chord) },
                                    selectedInstrument = uiState.instrument,
                                    onInstrumentChange = { instrument -> onInstrumentSelected(instrument) },
                                    strumSpeed = strumSpeed,
                                    onStrumSpeedChange = { onStrumSpeedChange(it) },
                                    selectedFavorite = uiState.selectedFavoriteChord,
                                    onSelectFavorite = { fav -> studioViewModel.selectFavorite(fav) },
                                    onAddFavoriteToSong = { fav -> studioViewModel.addFavoriteToSong(fav) },
                                    onRemoveFavorite = { fav -> studioViewModel.removeFavorite(fav) },
                                    onEditFavorite = { fav -> studioViewModel.onEditFavoriteClicked(fav) },
                                    onPlayFavorite = { fav -> studioViewModel.playFavoriteChord(fav) },
                                    onAddNewFavorite = { studioViewModel.initChordCreator(null); studioViewModel.onShowChordCreatorSheetChange(true) },
                                    progressions = currentSong.progressions,
                                    activeProgressionId = if (uiState.itemToSet?.type == "progression") uiState.itemToSet.id else null,
                                    onProgressionClick = { id -> 
                                        val progression = currentSong.progressions.find { it.id == id }
                                        if (progression != null) {
                                            val item = TrackItem(
                                                id = progression.id,
                                                beat = 0f,
                                                duration = 1f,
                                                type = "progression",
                                                data = TrackData(
                                                    name = "Progression: " + progression.name,
                                                    notes = progression.chordNames
                                                )
                                            )
                                            onArmItem(item)
                                        }
                                    },
                                    onAddProgressionClick = {
                                        // TODO: Open progression builder in Studio
                                    }
                                )
                            }
                            "Recordings" -> {
                                val recordings = uiState.recordingsWithNames
                                val playingId = uiState.playingRecordingId

                                if (recordings.isEmpty()) {
                                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                        Text("No recordings found for this song.", color = MaterialTheme.colorScheme.onSurfaceVariant)
                                    }
                                } else {
                                    LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                        items(recordings, key = { it.id }) { recording ->
                                            val isArmed = itemToSet?.type == "recording" && itemToSet.data.id == recording.id
                                            OutlinedButton(
                                                onClick = {
                                                    // PRECISE BEAT CALCULATION: (durationMs / 1000.0) * (bpm / 60.0)
                                                    val bpm = uiState.settings.bpm.toFloat().coerceAtLeast(1f)
                                                    val lengthInBeats = (recording.durationMs / 1000f) * (bpm / 60f)
                                                    
                                                    val recordingItem = TrackItem(
                                                        id = uuid4().toString(),
                                                        beat = 0f,
                                                        duration = lengthInBeats,
                                                        originalDurationBeats = lengthInBeats,
                                                        type = "recording",
                                                        data = TrackData(
                                                            id = recording.id,
                                                            name = recording.name,
                                                            url = recording.url
                                                        )
                                                    )
                                                    onArmItem(recordingItem)
                                                },
                                                modifier = Modifier.fillMaxWidth(),
                                                shape = RoundedCornerShape(8.dp),
                                                contentPadding = PaddingValues(0.dp),
                                                colors = if (isArmed) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(),
                                                border = if (!isArmed) BorderStroke(1.dp, MaterialTheme.colorScheme.outline) else null
                                            ) {
                                                Row(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .padding(horizontal = 16.dp, vertical = 8.dp),
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    horizontalArrangement = Arrangement.SpaceBetween
                                                ) {
                                                    Text(
                                                        text = recording.name,
                                                        maxLines = 1,
                                                        overflow = TextOverflow.Ellipsis,
                                                        modifier = Modifier.weight(1f)
                                                    )
                                                    IconButton(onClick = { studioViewModel.onPlayRecordingClicked(recording) }) {
                                                        Icon(
                                                            imageVector = if (playingId == recording.id) Icons.Default.Pause else Icons.Default.PlayArrow,
                                                            contentDescription = "Play/Pause",
                                                            tint = MaterialTheme.colorScheme.primary
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            "Notes" -> {
                                Column(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .verticalScroll(rememberScrollState())
                                        .padding(horizontal = 8.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 8.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.SpaceBetween
                                    ) {
                                        if (uiState.instrument == "guitar") {
                                            IconButton(onClick = { studioViewModel.toggleGuitarInputMode() }) {
                                                if (uiState.guitarInputMode == GuitarInputMode.KEYBOARD) {
                                                    Icon(painterResource(Res.drawable.ic_guitar), "Fretboard")
                                                } else {
                                                    Icon(Icons.Default.Piano, "Keyboard")
                                                }
                                            }
                                        } else {
                                            Spacer(Modifier.width(48.dp))
                                        }

                                        Row(
                                            modifier = Modifier
                                                .weight(1f)
                                                .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(8.dp))
                                                .padding(4.dp),
                                            horizontalArrangement = Arrangement.SpaceEvenly,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            val instruments = listOf("piano", "guitar", "bass", "drums")
                                            instruments.forEach { instrumentName ->
                                                val isSelected = uiState.instrument == instrumentName
                                                TextButton(
                                                    onClick = { onInstrumentSelected(instrumentName) },
                                                    shape = RoundedCornerShape(6.dp),
                                                    modifier = Modifier.weight(1f),
                                                    colors = ButtonDefaults.textButtonColors(
                                                        containerColor = if (isSelected) MaterialTheme.colorScheme.primary else Color.Transparent
                                                    )
                                                ) {
                                                    when (instrumentName) {
                                                        "piano" -> Icon(Icons.Default.Piano, "Piano", tint = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant)
                                                        "guitar" -> Icon(painterResource(Res.drawable.ic_guitar), "Guitar", tint = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant)
                                                        "bass" -> Icon(painterResource(Res.drawable.ic_bass), "Bass", tint = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant)
                                                        "drums" -> Icon(painterResource(Res.drawable.ic_drum), "Drums", tint = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant)
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    if (uiState.instrument == "guitar" && uiState.guitarInputMode == GuitarInputMode.FRETBOARD) {
                                        StudioFretboard(
                                            modifier = Modifier.fillMaxWidth(),
                                            selectedNotes = uiState.selectedNotes,
                                            selectedGuitarPositions = uiState.selectedGuitarPositions,
                                            onNoteClick = { note, pos -> studioViewModel.onNoteClicked(note, pos) },
                                            tuning = song?.guitarTuning ?: ChordUtils.DEFAULT_GUITAR_TUNING,
                                            capo = song?.guitarCapo ?: 0,
                                            fretWindowStart = uiState.fretWindowStart,
                                            onFretWindowChange = { studioViewModel.onFretWindowChange(it) },
                                            onClearSelectedNotes = onClearSelectedNotes,
                                            isLeftHanded = uiState.isLeftHanded
                                        )
                                    } else {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.SpaceBetween
                                        ) {
                                            Row(
                                                modifier = Modifier.weight(1f),
                                                verticalAlignment = Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.Center
                                            ) {
                                                IconButton(
                                                    onClick = { studioViewModel.changeKeyboardOctave(-1) },
                                                    enabled = uiState.keyboardOctave > 0
                                                ) {
                                                    Icon(Icons.Default.ChevronLeft, "Prev Octave")
                                                }
                                                Text(
                                                    "Octave ${uiState.keyboardOctave}",
                                                    textAlign = TextAlign.Center,
                                                    fontFamily = FontFamily.Monospace,
                                                    modifier = Modifier.padding(horizontal = 4.dp)
                                                )
                                                IconButton(
                                                    onClick = { studioViewModel.changeKeyboardOctave(1) },
                                                    enabled = uiState.keyboardOctave < 7
                                                ) {
                                                    Icon(Icons.Default.ChevronRight, "Next Octave")
                                                }
                                            }

                                            TextButton(
                                                onClick = onClearSelectedNotes,
                                                enabled = uiState.selectedNotes.isNotEmpty()
                                            ) {
                                                Text("Clear")
                                            }
                                        }

                                        PianoKeyboard(
                                            modifier = Modifier.fillMaxWidth().height(120.dp),
                                            octave = uiState.keyboardOctave,
                                            highlightedNotes = uiState.selectedNotes,
                                            onKeyClick = { note -> studioViewModel.onNoteClicked(note) }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }



@Composable
fun TimelineGrid(
    modifier: Modifier = Modifier,
    totalBars: Int,
    beatsPerBar: Int,
    dpPerBeat: Dp,
    trackCount: Int,
    trackHeight: Dp,
    isSnappingOn: Boolean,
    snapInterval: StepInterval
) {
    val totalBeats = totalBars * beatsPerBar
    val barLineColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.7f)
    val beatLineColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
    val trackLineColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
    val snapLineColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f)

    Canvas(modifier = modifier) {
        val trackHeightPx = trackHeight.toPx()
        for (i in 1 until trackCount) {
            val y = i * trackHeightPx
            drawLine(color = trackLineColor, start = Offset(0f, y), end = Offset(size.width, y))
        }
        for (beat in 0..totalBeats) {
            val x = beat * dpPerBeat.toPx()
            drawLine(
                color = if (beat % beatsPerBar == 0) barLineColor else beatLineColor,
                start = Offset(x, 0f),
                end = Offset(x, size.height)
            )
        }
        
        if (isSnappingOn) {
            val snapBeats = if (snapInterval.denominator > 0) 
                snapInterval.numerator.toFloat() / snapInterval.denominator 
            else 0f
            
            if (snapBeats > 0) {
                var currentBeat = 0f
                while (currentBeat <= totalBeats) {
                    currentBeat += snapBeats
                    // Skip whole beats as they are already drawn by the main loop
                    // Use epsilon for float comparison safety
                    if (abs(currentBeat % 1f) < 0.001f) continue 
                    
                    val x = currentBeat * dpPerBeat.toPx()
                    drawLine(
                        color = snapLineColor,
                        start = Offset(x, 0f),
                        end = Offset(x, size.height)
                    )
                }
            }
        }
    }
}

@Composable
fun IntervalSliderDialog(
    initialInterval: StepInterval,
    label: String,
    onDismiss: () -> Unit,
    onSave: (StepInterval) -> Unit
) {
    var localInterval by remember { mutableStateOf(initialInterval) }
    var localNumerator by remember { mutableStateOf(initialInterval.numerator.toString()) }
    var localDenominator by remember { mutableStateOf(initialInterval.denominator.toString()) }

    fun safeToInt(s: String, fallback: Int): Int {
        return s.filter { it.isDigit() }.toIntOrNull() ?: fallback
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        confirmButton = { 
            Button(
                onClick = { onSave(localInterval) },
                enabled = localInterval.denominator > 0 && localInterval.numerator > 0
            ) { Text("OK") } 
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } },
        title = { Text(label) },
        text = {
            Column(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)) {
                Row(modifier = Modifier.fillMaxWidth()) {
                    val isBar = localInterval.base == "bar"
                    OutlinedButton(
                        onClick = { localInterval = localInterval.copy(base = "bar") },
                        modifier = Modifier.weight(1f),
                        colors = if (isBar) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors()
                    ) { Text("Bar") }
                    Spacer(Modifier.width(8.dp))
                    OutlinedButton(
                        onClick = { localInterval = localInterval.copy(base = "beat") },
                        modifier = Modifier.weight(1f),
                        colors = if (!isBar) ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors()
                    ) { Text("Beat") }
                }
                Spacer(modifier = Modifier.height(16.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    OutlinedTextField(
                        value = localNumerator,
                        onValueChange = { 
                            localNumerator = it 
                            val num = safeToInt(it, 1)
                            localInterval = localInterval.copy(numerator = num)
                        },
                        modifier = Modifier.width(70.dp),
                        textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                        keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.Number),
                        singleLine = true
                    )
                    Text("/", modifier = Modifier.padding(horizontal = 8.dp), style = MaterialTheme.typography.titleLarge)
                    OutlinedTextField(
                        value = localDenominator,
                        onValueChange = { 
                            localDenominator = it 
                            val den = safeToInt(it, 1)
                            localInterval = localInterval.copy(denominator = den)
                        },
                        modifier = Modifier.width(70.dp),
                        textStyle = LocalTextStyle.current.copy(textAlign = androidx.compose.ui.text.style.TextAlign.Center),
                        keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.Number),
                        singleLine = true
                    )
                }
            }
        }
    )
}

private fun getNoteMidiValue(noteName: String): Int {
    if (noteName.isBlank()) return -1
    val notePart = noteName.dropLast(1)
    val octavePart = noteName.last().toString().toIntOrNull() ?: return -1
    val noteIndex = ChordUtils.ROOT_NOTES.indexOf(notePart)
    if (noteIndex == -1) return -1
    return 12 * octavePart + noteIndex
}

private fun calculateItemLayout(
    items: List<TrackItem>,
    chordLibraryOrder: List<String>
): List<LayoutItem> {
    if (items.isEmpty()) return emptyList()

    val itemsSortedByBeat = items.sortedBy { it.beat }
    val finalLayout = mutableListOf<LayoutItem>()
    val epsilon = 0.001f

    // 1. Group items that form a continuous chain of overlaps
    val overlapGroups = mutableListOf<MutableList<TrackItem>>()
    if (itemsSortedByBeat.isNotEmpty()) {
        var currentGroup = mutableListOf(itemsSortedByBeat.first())
        var currentGroupEnd = itemsSortedByBeat.first().let { it.beat + it.duration }

        for (i in 1 until itemsSortedByBeat.size) {
            val item = itemsSortedByBeat[i]
            // Use epsilon to ignore micro-overlaps (treat as touching)
            if (item.beat < (currentGroupEnd - epsilon)) {
                currentGroup.add(item)
                currentGroupEnd = max(currentGroupEnd, item.beat + item.duration)
            } else {
                overlapGroups.add(currentGroup)
                currentGroup = mutableListOf(item)
                currentGroupEnd = item.beat + item.duration
            }
        }
        overlapGroups.add(currentGroup)
    }

    // 2. Process each overlap group independently
    for (group in overlapGroups) {
        val sortedGroup = group.sortedWith(compareBy(
            // Prioritize notes over other types
            { item -> if (item.type == "note") 0 else 1 },
            // Sort chords by their order in the library
            { item ->
                if (item.type == "chord") {
                    -(chordLibraryOrder.indexOf(item.data.name).let { if (it == -1) Int.MAX_VALUE else it })
                } else {
                    0
                }
            },
            // Sort notes by their MIDI value (highest pitch first)
            { item ->
                if (item.type == "note") {
                    -(item.data.notes?.minOfOrNull { getNoteMidiValue(it) } ?: Int.MAX_VALUE)
                } else {
                    0
                }
            },
            // Finally, sort by beat
            { it.beat }
        ))

        val lanes = mutableListOf<MutableList<TrackItem>>()
        val groupLayouts = mutableListOf<LayoutItem>()

        for (item in sortedGroup) {
            var assignedLaneIndex = -1
            for (i in lanes.indices) {
                val lastItemInLane = lanes[i].lastOrNull()
                // Check if item starts after (or at) the end of the last item, with epsilon leniency
                if (lastItemInLane == null || item.beat >= ((lastItemInLane.beat + lastItemInLane.duration) - epsilon)) {
                    assignedLaneIndex = i
                    break
                }
            }

            if (assignedLaneIndex == -1) {
                assignedLaneIndex = lanes.size
                lanes.add(mutableListOf())
            }

            lanes[assignedLaneIndex].add(item)
            groupLayouts.add(LayoutItem(item, assignedLaneIndex, 0))
        }

        val numLanesForGroup = lanes.size
        groupLayouts.forEach { layout ->
            finalLayout.add(layout.copy(lanesInGroup = numLanesForGroup))
        }
    }

    val map = finalLayout.associateBy { it.item.id }
    return items.mapNotNull { map[it.id] }
}

@Composable
fun PianoKeyboard(
    modifier: Modifier = Modifier,
    octave: Int,
    highlightedNotes: List<String>,
    onKeyClick: (String) -> Unit
) {
    Box(modifier = modifier) {
        // White Keys
        Row(modifier = Modifier.fillMaxSize()) {
            ChordUtils.PIANO_KEYS_IN_OCTAVE.filter { !it.second }.forEach { (noteName, _) ->
                val fullNoteName = "$noteName$octave"
                val isHighlighted = highlightedNotes.contains(fullNoteName)
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .border(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.5f))
                        .background(if (isHighlighted) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onPrimary)
                        .clickable { onKeyClick(fullNoteName) }
                )
            }
        }
        // Black Keys
        Row(modifier = Modifier.fillMaxSize().padding(horizontal = 8.dp)) {
            Spacer(modifier = Modifier.weight(0.6f))
            BlackKey(note = "Db$octave", highlighted = highlightedNotes.contains("Db$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Eb$octave", highlighted = highlightedNotes.contains("Eb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(1.6f))
            BlackKey(note = "Gb$octave", highlighted = highlightedNotes.contains("Gb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Ab$octave", highlighted = highlightedNotes.contains("Ab$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Bb$octave", highlighted = highlightedNotes.contains("Bb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.6f))
        }
    }
}

@Composable
fun RowScope.BlackKey(note: String, highlighted: Boolean, onClick: (String) -> Unit) {
    Box(
        modifier = Modifier
            .weight(0.8f)
            .fillMaxHeight(0.6f)
            .padding(horizontal = 2.dp)
            .clip(RoundedCornerShape(bottomStart = 4.dp, bottomEnd = 4.dp))
            .background(if (highlighted) MaterialTheme.colorScheme.primary else Color.Black)
            .border(1.dp, Color.Black)
            .clickable { onClick(note) }
    )
}

private fun getNoteForFret(stringIndex: Int, fret: Int, tuning: List<String>, capo: Int): String? {
    if (fret < 0) return null
    val openNote = tuning.getOrNull(stringIndex) ?: return null
    val openMidi = ChordUtils.noteNameToMidi(openNote) ?: return null
    val finalMidi = if (fret == 0) openMidi + capo else openMidi + fret
    return ChordUtils.midiToNoteName(finalMidi)
}

@Composable
fun StudioFretboard(
    modifier: Modifier = Modifier,
    selectedNotes: List<String>,
    selectedGuitarPositions: Set<Pair<Int, Int>>,
    onNoteClick: (String, Pair<Int, Int>?) -> Unit,
    tuning: List<String>,
    capo: Int,
    fretWindowStart: Int,
    onFretWindowChange: (Int) -> Unit,
    onClearSelectedNotes: () -> Unit,
    isLeftHanded: Boolean = false
) {
    val numFretsToShow = 5
    val primaryColor = MaterialTheme.colorScheme.primary
    val reversedTuning = remember(tuning) { tuning.reversed() }
    val fretboardHeight = 180.dp

    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = modifier) {
        val layoutDirection = if (isLeftHanded) androidx.compose.ui.unit.LayoutDirection.Rtl else androidx.compose.ui.unit.LayoutDirection.Ltr
        CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides layoutDirection) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Tuning Labels - Force LTR
                CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides androidx.compose.ui.unit.LayoutDirection.Ltr) {
                    Column(
                        modifier = Modifier.height(fretboardHeight),
                        verticalArrangement = Arrangement.SpaceEvenly,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        reversedTuning.forEach { note ->
                            Text(
                                note.dropLast(1),
                                textAlign = TextAlign.Center,
                                modifier = Modifier.width(24.dp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                // Open String Toggles
                Column(
                    modifier = Modifier
                        .height(fretboardHeight)
                        .padding(horizontal = 4.dp),
                    verticalArrangement = Arrangement.SpaceEvenly,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    (0..5).forEach { reversedStringIndex ->
                        val actualStringIndex = 5 - reversedStringIndex
                        val openStringNote = getNoteForFret(actualStringIndex, 0, tuning, capo)
                        // Use positions if available, fall back to note name (for legacy/piano interop)
                        val isSelected = selectedGuitarPositions.contains(Pair(actualStringIndex, 0)) || 
                                         (selectedGuitarPositions.isEmpty() && openStringNote in selectedNotes)
                        
                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clickable { if (openStringNote != null) onNoteClick(openStringNote, Pair(actualStringIndex, 0)) },
                            contentAlignment = Alignment.Center
                        ) {
                            if (isSelected) {
                                Box(modifier = Modifier.size(12.dp).background(primaryColor, CircleShape))
                            } else {
                                Box(modifier = Modifier.size(12.dp).border(1.dp, MaterialTheme.colorScheme.onSurfaceVariant, CircleShape))
                            }
                        }
                    }
                }

                // Fretboard Canvas
                Canvas(modifier = Modifier
                    .weight(1f)
                    .height(fretboardHeight)
                    .pointerInput(isLeftHanded, fretWindowStart) {
                        detectTapGestures { offset ->
                            val stringHeight = size.height / 6f
                            val reversedStringIndex = (offset.y / stringHeight).toInt().coerceIn(0, 5)
                            val actualStringIndex = 5 - reversedStringIndex
                            
                            val effectiveX = if (isLeftHanded) size.width - offset.x else offset.x
                            val fretWidth = size.width / numFretsToShow.toFloat()
                            val fretIndex = (effectiveX / fretWidth).toInt().coerceIn(0, numFretsToShow - 1)
                            val fretNumber = fretWindowStart + fretIndex + 1
                            if (fretNumber <= capo) return@detectTapGestures
                            getNoteForFret(actualStringIndex, fretNumber, tuning, capo)?.let { note -> 
                                onNoteClick(note, Pair(actualStringIndex, fretNumber)) 
                            }
                        }
                    }
                ) {
                    val numStrings = 6
                    val stringHeight = size.height / numStrings.toFloat()
                    val fretWidth = size.width / numFretsToShow.toFloat()

                    drawRect(color = Color(0xFF525252))
                    (0..numFretsToShow).forEach { fret ->
                        val rawX = fret * fretWidth
                        val x = if (isLeftHanded) size.width - rawX else rawX
                        val stroke = if (fret == 0 && capo == 0) 6f else 2f
                        drawLine(Color.Gray, start = Offset(x, 0f), end = Offset(x, size.height), strokeWidth = stroke)
                    }
                    (0 until numStrings).forEach { string ->
                        val y = (string + 0.5f) * stringHeight
                        drawLine(Color.LightGray, start = Offset(0f, y), end = Offset(size.width, y), strokeWidth = (string * 0.4f) + 1.5f)
                    }
                    // Capo drawing
                    if (capo > 0 && capo >= fretWindowStart && capo < fretWindowStart + numFretsToShow) {
                        val capoFretPosIndex = capo - fretWindowStart
                        val rawXCenter = (capoFretPosIndex * fretWidth) - (fretWidth / 2f)
                        val xCenter = if (isLeftHanded) size.width - rawXCenter else rawXCenter
                        val barWidth = 12.dp.toPx()
                        drawRoundRect(
                            color = primaryColor.copy(alpha = 0.9f),
                            topLeft = Offset((xCenter - (barWidth / 2)).toFloat(), -4.dp.toPx()),
                            size = Size(barWidth, size.height + 8.dp.toPx()),
                            cornerRadius = CornerRadius(6.dp.toPx())
                        )
                    }
                    // Dots drawing
                    val markerFrets = listOf(3, 5, 7, 9, 12, 15, 17, 19, 21)
                    (0 until numFretsToShow).forEach { fretIndex ->
                        val fretNumber = fretWindowStart + fretIndex + 1
                        if (markerFrets.contains(fretNumber)) {
                            val rawX = (fretIndex * fretWidth) + (fretWidth / 2f)
                            val x = if (isLeftHanded) size.width - rawX else rawX
                            val isDoubleDot = fretNumber % 12 == 0
                            if (isDoubleDot) {
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height * 0.25f))
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height * 0.75f))
                            } else {
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height / 2f))
                            }
                        }
                    }
                    // Notes drawing
                    selectedGuitarPositions.forEach { (string, fret) ->
                        if (fret > 0 && fret > fretWindowStart && fret <= fretWindowStart + numFretsToShow) {
                            val fretPosIndex = fret - fretWindowStart - 1
                            val rawX = (fretPosIndex * fretWidth) + (fretWidth / 2f)
                            val x = if (isLeftHanded) size.width - rawX else rawX
                            val visualStringIndex = 5 - string
                            val y = (visualStringIndex + 0.5f) * stringHeight
                            drawCircle(color = primaryColor, radius = stringHeight * 0.35f, center = Offset(x, y))
                        }
                    }
                }
            }
        }

        // Fret window controls
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            // Swap controls based on handedness
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center
            ) {
                if (isLeftHanded) {
                    IconButton(onClick = { onFretWindowChange(1) }, enabled = fretWindowStart < 18) {
                        Icon(Icons.Default.ChevronLeft, "Shift Left")
                    }
                } else {
                    IconButton(onClick = { onFretWindowChange(-1) }, enabled = fretWindowStart > 0) {
                        Icon(Icons.Default.ChevronLeft, "Shift Left")
                    }
                }
                
                Text("Fret: ${fretWindowStart + 1}", fontFamily = FontFamily.Monospace, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(horizontal = 4.dp))
                
                if (isLeftHanded) {
                    IconButton(onClick = { onFretWindowChange(-1) }, enabled = fretWindowStart > 0) {
                        Icon(Icons.Default.ChevronRight, "Shift Right")
                    }
                } else {
                    IconButton(onClick = { onFretWindowChange(1) }, enabled = fretWindowStart < 18) {
                        Icon(Icons.Default.ChevronRight, "Shift Right")
                    }
                }
            }
            TextButton(onClick = onClearSelectedNotes, enabled = selectedNotes.isNotEmpty()) {
                Text("Clear")
            }
        }
    }
}

@Composable
fun SelectionContextMenu(
    count: Int,
    onDelete: () -> Unit,
    onCancel: () -> Unit,
    onNudge: (Int) -> Unit,
    onCopy: () -> Unit,
    onLassoModeToggled: () -> Unit,
    isLassoModeActive: Boolean
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .height(80.dp)
            .navigationBarsPadding()
            .tutorialTarget(TutorialStep.STUDIO_CONTEXT_MENU),
        shadowElevation = 8.dp,
        color = MaterialTheme.colorScheme.surfaceColorAtElevation(3.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text("$count selected", fontWeight = FontWeight.Bold)
            Spacer(Modifier.width(16.dp))

            IconButton(onClick = { onNudge(-1) }) { Icon(Icons.Default.ChevronLeft, "Nudge Left") }
            IconButton(onClick = { onNudge(1) }) { Icon(Icons.Default.ChevronRight, "Nudge Right") }

            IconButton(onClick = onLassoModeToggled) {
                Icon(
                    Icons.Filled.AutoFixNormal,
                    contentDescription = "Lasso Select",
                    tint = if (isLassoModeActive) MaterialTheme.colorScheme.primary else LocalContentColor.current
                )
            }

            IconButton(onClick = onCopy) { Icon(Icons.Default.ContentCopy, "Copy") }
            IconButton(onClick = onDelete) { Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error) }

            IconButton(onClick = onCancel) { Icon(Icons.Default.Close, "Deselect All") }
        }
    }
}

@Composable
fun LassoOverlay(
    isLassoModeActive: Boolean,
    lassoPath: List<Offset>,
    onLassoDrawStart: (Offset) -> Unit,
    onLassoDraw: (Offset) -> Unit,
    onLassoDrawEnd: (pixelsPerBeat: Float, trackHeightPx: Float) -> Unit,
    modifier: Modifier = Modifier,
    horizontalScrollState: ScrollState,
    verticalScrollState: ScrollState,
    pixelsPerBeat: Float,
    trackHeightPx: Float
) {
    if (isLassoModeActive) {
        val lassoColor = MaterialTheme.colorScheme.primary
        Canvas(
            modifier = modifier
                .pointerInput(Unit) {
                    detectDragGestures(
                        onDragStart = { startOffset ->
                            onLassoDrawStart(startOffset)
                        },
                        onDragEnd = {
                            onLassoDrawEnd(pixelsPerBeat, trackHeightPx)
                        },
                        onDragCancel = {
                            onLassoDrawEnd(pixelsPerBeat, trackHeightPx)
                        },
                        onDrag = { change, _ ->
                            change.consume()
                            onLassoDraw(change.position)
                        }
                    )
                }
        ) {
            if (lassoPath.size > 1) {
                val contentPath = Path().apply {
                    moveTo(lassoPath.first().x, lassoPath.first().y)
                    lassoPath.drop(1).forEach { point ->
                        lineTo(point.x, point.y)
                    }
                    if (lassoPath.size > 2) {
                        close()
                    }
                }
                drawPath(
                    path = contentPath,
                    color = lassoColor.copy(alpha = 0.2f)
                )
                drawPath(
                    path = contentPath,
                    color = lassoColor,
                    style = Stroke(
                        width = 2.dp.toPx(),
                        cap = StrokeCap.Round,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(10f, 10f), 0f)
                    )
                )
            }
        }
    }
}

@Composable
private fun StepSettingControl(
    label: String,
    interval: StepInterval,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        modifier = modifier
    ) {
        Text("$label:", style = MaterialTheme.typography.labelSmall)
        OutlinedButton(
            onClick = onClick,
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
            modifier = Modifier.height(28.dp)
        ) {
            Text(
                "${interval.numerator}/${interval.denominator}",
                fontFamily = FontFamily.Monospace,
                style = MaterialTheme.typography.labelMedium
            )
        }
    }
}

